'use client';

/**
 * ContextRailProvider + useContextRail()
 *
 * Provides a per-page context for opening dynamic tabs in the rail. Pages
 * that previously mounted a separate `<SplitPanel>` for create/edit can
 * call `openTab({...})` instead — the rail will append a closable tab,
 * switch to it, and expand. Closing the tab (`closeTab(id)` or the X
 * affordance) drops it from the tab list and reverts to the page's
 * default fixed tab.
 *
 * The provider is mounted by each page-specific rail wrapper
 * (`<LoanContextRail>`, `<BorrowerContextRail>`, etc.) so the tabs the
 * provider exposes are the union of (a) the page's static tabs (Activity
 * / Messages / Related) and (b) any dynamic tabs the page or its children
 * have opened.
 *
 * Issue #1472, PR #1477 design feedback (consolidate right-edge surfaces).
 */

import type React from 'react';
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ContextRailTab } from './ContextRail';

export interface DynamicContextRailTab extends ContextRailTab {
  /** Tab body (rendered when the tab is active). */
  content: React.ReactNode;
  /**
   * If true, the tab can be closed by the user via an X affordance and
   * will be removed from the tab list. Static page tabs (Activity etc.)
   * leave this false. Dynamic tabs added via `openTab()` default to true.
   */
  closable?: boolean;
}

interface ContextRailContextValue {
  /** All currently-open dynamic tabs (in insertion order). */
  dynamicTabs: DynamicContextRailTab[];
  /**
   * Open or upsert a dynamic tab. If a tab with the same `id` already
   * exists, its content + label + count are replaced (so callers don't
   * need to track open state — calling `openTab` is idempotent). The
   * rail switches to the tab and expands. Returns the tab id.
   */
  openTab: (tab: DynamicContextRailTab) => string;
  /** Remove a dynamic tab by id. */
  closeTab: (id: string) => void;
  /** Active tab id (read by `<ContextRail>`). */
  activeTabId: string | null;
  /** Setter (read by `<ContextRail>`). */
  setActiveTabId: (id: string) => void;
}

const ContextRailContext = createContext<ContextRailContextValue | null>(null);

export interface ContextRailProviderProps {
  children: React.ReactNode;
}

export function ContextRailProvider({ children }: ContextRailProviderProps): React.ReactElement {
  const [dynamicTabs, setDynamicTabs] = useState<DynamicContextRailTab[]>([]);
  const [activeTabId, setActiveTabIdState] = useState<string | null>(null);

  const openTab = useCallback((tab: DynamicContextRailTab) => {
    setDynamicTabs((prev) => {
      const idx = prev.findIndex((t) => t.id === tab.id);
      if (idx >= 0) {
        // Upsert — replace in place to preserve order
        const next = prev.slice();
        next[idx] = { closable: true, ...tab };
        return next;
      }
      return [...prev, { closable: true, ...tab }];
    });
    setActiveTabIdState(tab.id);
    return tab.id;
  }, []);

  const closeTab = useCallback((id: string) => {
    setDynamicTabs((prev) => prev.filter((t) => t.id !== id));
    // Active-tab fallback handled by <ContextRail> — it reverts to the
    // page's default tab when activeTabId no longer matches anything.
    setActiveTabIdState((current) => (current === id ? null : current));
  }, []);

  const value = useMemo<ContextRailContextValue>(
    () => ({
      dynamicTabs,
      openTab,
      closeTab,
      activeTabId,
      setActiveTabId: setActiveTabIdState,
    }),
    [dynamicTabs, openTab, closeTab, activeTabId],
  );

  return <ContextRailContext.Provider value={value}>{children}</ContextRailContext.Provider>;
}

/**
 * Read the rail's dynamic-tab context. Throws if called outside a
 * `<ContextRailProvider>` — pages that haven't mounted a rail shouldn't
 * be calling this. If you need a graceful no-op (e.g. a shared component
 * that may render with or without a rail), use `useOptionalContextRail`.
 */
export function useContextRail(): ContextRailContextValue {
  const ctx = useContext(ContextRailContext);
  if (!ctx) {
    throw new Error('useContextRail must be called inside a <ContextRailProvider>');
  }
  return ctx;
}

/**
 * Same as `useContextRail` but returns `null` when no provider is
 * present. Useful for shared components that render in pages with and
 * without a rail.
 */
export function useOptionalContextRail(): ContextRailContextValue | null {
  return useContext(ContextRailContext);
}
